import { useMemo, useState } from 'react';

import { useActiveOrg } from '../../../active-org';
import { useAuth, useTeams } from '../../../api';
import { Select } from '../../../components/Select';
import { EmptyState } from '../../components/EmptyState';
import { Skeleton } from '../../components/Skeleton';
import { useAccountingView } from '../../hooks/useAccountingView';
import { buildTeamGrouping } from '../../team-filter';

import styles from './Accounting.module.css';
import { ACCOUNTING_EMPTY } from './empty';
import { InvestmentByFunctionView } from './InvestmentByFunctionView';
import { ReviewQueueRail } from './ReviewQueueRail';
import { buildSankeyGraph, type LeftAxis, type RightAxis } from './sankey-data';
import { SpendSankey } from './SpendSankey';
import { TeamsView } from './TeamsView';
import { TreatmentByEngineerView } from './TreatmentByEngineerView';
import { TreatmentByFunctionView } from './TreatmentByFunctionView';

const LEFT_OPTIONS: Array<{ value: LeftAxis; label: string }> = [
  { value: 'function', label: 'Business function' },
  { value: 'team', label: 'Team' },
];

const RIGHT_OPTIONS: Array<{ value: RightAxis; label: string }> = [
  { value: 'investment', label: 'Investment area' },
  { value: 'treatment', label: 'Treatment' },
];

/**
 * Accounting overview: the spend Sankey + its matching detail table on the
 * left, the review queue docked to the right (Timeline-style rail). Two
 * selectors drive both the diagram's axes and which table renders below:
 *
 *   left  → Business function | Team (user-defined engineer teams)
 *   right → Investment area    | Treatment
 */
export function AccountingOverview() {
  const [leftAxis, setLeftAxis] = useState<LeftAxis>('function');
  const [rightAxis, setRightAxis] = useState<RightAxis>('investment');

  const { data, isPending, error } = useAccountingView();

  // User-defined engineer teams drive the "Team" left axis (replacing the
  // old human-vs-agent split). With only the default team, all spend flows
  // from a single "All engineers" node.
  const auth = useAuth();
  const orgs = auth.status === 'signed-in' ? auth.me.orgs : [];
  const { active } = useActiveOrg(orgs);
  const teams = useTeams(active?.slug ?? null).data ?? [];
  const teamGrouping = useMemo(() => buildTeamGrouping(teams), [teams]);

  const graph = useMemo(
    () => (data ? buildSankeyGraph(data, leftAxis, rightAxis, teamGrouping) : null),
    [data, leftAxis, rightAxis, teamGrouping],
  );

  return (
    <div className={styles.overview}>
      <div className={styles.overviewMain}>
        <div className={styles.axisControls}>
          <span className={styles.axisCaption}>Spend by</span>
          <Select
            label='Group spend on the left by'
            value={leftAxis}
            onChange={(e) => setLeftAxis(e.target.value as LeftAxis)}
          >
            {LEFT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          <span className={styles.axisBy}>by</span>
          <Select
            label='Break spend on the right down by'
            value={rightAxis}
            onChange={(e) => setRightAxis(e.target.value as RightAxis)}
          >
            {RIGHT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </div>

        <div className={styles.sankeyWrap}>
          {isPending ? (
            <Skeleton title='Spend flow' rows={4} />
          ) : error ? (
            <div className={styles.errorBox}>
              Failed to load accounting view: {error.message}
            </div>
          ) : !data || data.commits.length === 0 ? (
            <EmptyState {...ACCOUNTING_EMPTY} />
          ) : graph && graph.links.length > 0 ? (
            <SpendSankey data={graph} />
          ) : (
            <p className='muted'>No spend to chart for this period.</p>
          )}
        </div>

        <div className={styles.tableWrap}>
          <TableForSelection left={leftAxis} right={rightAxis} />
        </div>
      </div>

      <ReviewQueueRail />
    </div>
  );
}

function TableForSelection({ left, right }: { left: LeftAxis; right: RightAxis }) {
  if (left === 'function') {
    return right === 'investment' ? <InvestmentByFunctionView /> : <TreatmentByFunctionView />;
  }
  return right === 'investment' ? <TeamsView /> : <TreatmentByEngineerView />;
}
